import { cn } from '../../utils/cn';
import { BAND_NAME, type LotBand } from '../../types/nurseryColour';

/**
 * A paint as a small square, its name riding along as the title and screen
 * reader text: a colour alone says nothing to someone who cannot see it.
 */
export function PaintSwatch({ hex, name, className }: { hex: string; name: string; className?: string }) {
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      className={cn('inline-block h-3.5 w-3.5 flex-shrink-0 rounded-sm border border-black/20', className)}
      style={{ backgroundColor: hex }}
    />
  );
}

/**
 * The bands a lot carries, bottom first. What is ON the trees is what shows:
 * a painting's own paint, which may differ from the scheme's now ("repaint").
 * A band not painted yet shows the colour it should get, hollow.
 *
 * `compact` drops the band names, for a pick card.
 */
function LotBands({ bands, compact = false, className }: { bands: LotBand[]; compact?: boolean; className?: string }) {
  if (!bands.length) return null;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-600', className)}>
      {bands.map((b) => {
        const on = b.painting;
        const hex = on ? on.paint_hex : b.paint?.hex;
        const name = on ? on.paint_name : b.paint?.name;
        if (!hex || !name) return null;
        const label = `${BAND_NAME[b.band]}: ${name}${!on ? ' (not painted yet)' : b.state === 'repaint' ? ` (now ${b.paint?.name})` : ''}`;
        return (
          <span key={b.band} className="inline-flex items-center gap-1 min-w-0" title={label}>
            <span
              role="img"
              aria-label={label}
              className={cn('inline-block h-3.5 w-3.5 flex-shrink-0 rounded-sm', on ? 'border border-black/20' : 'border-2 border-dashed')}
              style={on ? { backgroundColor: hex } : { borderColor: hex }}
            />
            {!compact && <span className="break-words">{BAND_NAME[b.band]} {name}</span>}
            {b.state === 'repaint' && <span className="text-amber-700">repaint</span>}
          </span>
        );
      })}
    </span>
  );
}

export default LotBands;
